/**
 * Executable zod mirror of the RFD v1 types in `../types.ts`.
 *
 * Normative sources: docs/04-form-schema-spec.md (property-by-property),
 * docs/schema/rasd-form.schema.json (the generated JSON Schema this must stay in
 * sync with) and docs/00-decisions-and-conventions.md §4.
 *
 * Two rules govern every object below:
 *  1. objects the JSON Schema marks `additionalProperties: false` use `.strict()`
 *     — but every one of them also accepts `ext` (docs/04 §12), and `appearance`
 *     plus `props` of `x:` elements stay open because they are extension points;
 *  2. the inferred output type must be assignable to the hand-written type in
 *     `../types.ts`. `assertMatchesTypes` at the bottom of this file is the
 *     compile-time proof.
 *
 * Unknown-key rejection here is *not* the same as "the runtime refuses the form":
 * `validateFormDefinition()` downgrades zod's `unrecognized_keys` to the
 * `W_UNKNOWN_PROPERTY` warning required by docs/04 §17 (ignore-and-preserve).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Primitives (docs/04 §1.3, §1.4)
// ---------------------------------------------------------------------------
/** Element / choice-list / dataset / calculated identifier grammar. */
export const NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
/** Page id and trigger id grammar (hyphens allowed). */
export const PAGE_ID_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;
/** Form slug. */
export const FORM_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
/** RFD spec version accepted by this client: MAJOR 1 only. */
export const RASD_VERSION_PATTERN = /^1\.(0|[1-9][0-9]*)$/;
/** Custom element type: `x:` + kebab-case. */
export const CUSTOM_TYPE_PATTERN = /^x:[a-z][a-z0-9-]*$/;
/** Choice value: 1–128 characters, no whitespace. */
export const CHOICE_VALUE_PATTERN = /^[^\s]{1,128}$/;
/** BCP 47 language tag (shape check only — no registry lookup). */
export const BCP47_PATTERN = /^[a-zA-Z]{2,8}(-[a-zA-Z0-9]{1,8})*$/;
/** Vendor key inside `ext`. */
export const EXT_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/;
/** Host action / custom validator id. */
export const HOST_ID_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_.:-]{0,63}$/;
/** Keys that must never appear anywhere in a definition (prototype pollution). */
export const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'];
export const NameSchema = z.string().regex(NAME_PATTERN).max(64);
export const PageIdSchema = z.string().regex(PAGE_ID_PATTERN).max(64);
export const ChoiceValueSchema = z.string().regex(CHOICE_VALUE_PATTERN);
export const Bcp47Schema = z.string().regex(BCP47_PATTERN);
/** REL v1 source text. Parsed (never eval'd) by the semantic pass. */
export const ExprSchema = z.string().min(1).max(4000);
export const LocalizedStringSchema = z.union([
    z.string().max(65536),
    z.record(Bcp47Schema, z.string().max(65536)),
]);
/**
 * Vendor payload. Opaque to Rasd, always round-tripped — but prototype-pollution
 * keys are rejected outright (docs/04 §2, research/11).
 */
export const ExtSchema = z
    .record(z.string().regex(EXT_KEY_PATTERN), z.unknown())
    .superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
        if (FORBIDDEN_KEYS.includes(key)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `Forbidden key "${key}"` });
        }
    }
});
export const SeveritySchema = z.enum(['error', 'warning', 'info']);
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
export const GeoSchema = z
    .object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    alt: z.number().optional(),
    accuracy: z.number().min(0).optional(),
    capturedAt: z.string(),
})
    .strict();
export const AttachmentRefSchema = z
    .object({
    attachmentId: z.string().min(1),
    name: z.string().optional(),
    mime: z.string().optional(),
    bytes: z.number().int().min(0).optional(),
    sha256: z.string().optional(),
    capturedAt: z.string().optional(),
    geo: GeoSchema.optional(),
})
    .strict();
export const MediaSchema = z
    .object({
    image: LocalizedStringSchema.optional(),
    audio: LocalizedStringSchema.optional(),
    video: LocalizedStringSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// settings (docs/04 §4)
// ---------------------------------------------------------------------------
export const LocaleMetaSchema = z
    .object({
    // Required here (unlike the JSON Schema) because `../types.ts` declares it required.
    dir: z.enum(['rtl', 'ltr']),
    numbering: z.enum(['latn', 'native']).optional(),
    calendar: z.enum(['gregorian', 'islamic-umalqura']).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const AuditLocationSchema = z
    .object({
    enabled: z.boolean(),
    // ODK provider-priority vocabulary (docs/04 §4.2). See `assertMatchesTypes`.
    priority: z.enum(['no-power', 'low-power', 'balanced', 'high-accuracy']).optional(),
    minSeconds: z.number().int().min(1).max(3600).optional(),
    minMeters: z.number().min(0).max(10000).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const AuditSettingsSchema = z
    .object({
    enabled: z.boolean(),
    trackChanges: z.boolean().optional(),
    changeReasons: z.enum(['never', 'onEdit']).optional(),
    identifyUser: z.boolean().optional(),
    location: AuditLocationSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const EncryptionSettingsSchema = z
    .object({
    mode: z.enum(['none', 'field', 'submission']),
    publicKeyId: z.string().min(1).max(128).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const ThemeSettingsSchema = z
    .object({
    themeId: z.string().regex(/^[a-z0-9][a-z0-9-]{1,63}$/).optional(),
    // A partial theme document; core deliberately does not model theme tokens.
    overrides: z.record(z.string(), z.unknown()).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const FormSettingsSchema = z
    .object({
    defaultLocale: Bcp47Schema,
    locales: z.array(Bcp47Schema).max(20),
    navigation: z.enum(['paged', 'scroll']).optional(),
    showProgress: z.boolean().optional(),
    allowDrafts: z.boolean().optional(),
    autosaveMs: z.number().int().min(0).max(60000).optional(),
    instanceName: ExprSchema.optional(),
    submissionIdPrefix: z.string().regex(/^[A-Z0-9]{1,8}$/).optional(),
    numbering: z.enum(['latn', 'native']).optional(),
    calendar: z.enum(['gregorian', 'islamic-umalqura']).optional(),
    localeMeta: z.record(Bcp47Schema, LocaleMetaSchema).optional(),
    audit: AuditSettingsSchema.optional(),
    encryption: EncryptionSettingsSchema.optional(),
    theme: ThemeSettingsSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// choiceLists and datasets (docs/04 §5, §6)
// ---------------------------------------------------------------------------
export const ChoiceSchema = z
    .object({
    value: ChoiceValueSchema,
    label: LocalizedStringSchema,
    media: MediaSchema.optional(),
    attrs: z
        .record(z.string().regex(NAME_PATTERN), z.union([z.string().max(512), z.number(), z.boolean()]))
        .optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const ChoiceListSchema = z
    .object({
    choices: z.array(ChoiceSchema).max(10000).optional(),
    source: z.object({ type: z.literal('dataset'), dataset: NameSchema }).strict().optional(),
    valueKey: NameSchema.optional(),
    labelKey: NameSchema.optional(),
    filterKeys: z.array(NameSchema).max(8).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const DatasetColumnSchema = z
    .object({
    name: NameSchema,
    type: z.enum(['string', 'number', 'boolean']).optional(),
    label: LocalizedStringSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const DatasetSchema = z
    .object({
    name: NameSchema,
    source: z.enum(['server', 'inline', 'url']),
    keyField: NameSchema,
    inline: z
        .array(z.record(z.string(), z.union([z.string().max(4000), z.number(), z.boolean(), z.null()])))
        .max(10000)
        .optional(),
    url: z.string().regex(/^https:\/\//).max(2048).optional(),
    columns: z.array(DatasetColumnSchema).max(200).optional(),
    minVersion: z.string().max(64).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// logic (docs/04 §8)
// ---------------------------------------------------------------------------
export const CalculatedValueSchema = z
    .object({
    name: NameSchema,
    calculate: ExprSchema,
    includeInData: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const TriggerActionSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('setValue'),
        target: NameSchema,
        value: z.unknown(),
        expr: ExprSchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z.object({ type: z.literal('clearValue'), target: NameSchema, ext: ExtSchema.optional() }).strict(),
    z
        .object({ type: z.literal('complete'), message: LocalizedStringSchema.optional(), ext: ExtSchema.optional() })
        .strict(),
    z.object({ type: z.literal('skipTo'), page: PageIdSchema, ext: ExtSchema.optional() }).strict(),
    z
        .object({
        type: z.literal('showMessage'),
        message: LocalizedStringSchema,
        severity: SeveritySchema.optional(),
        blocking: z.boolean().optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('custom'),
        id: z.string().regex(HOST_ID_PATTERN),
        payload: z.unknown(),
        ext: ExtSchema.optional(),
    })
        .strict(),
]);
export const TriggerSchema = z
    .object({
    id: PageIdSchema,
    when: ExprSchema,
    on: z.enum(['change', 'pageLeave', 'finalize']).optional(),
    once: z.boolean().optional(),
    actions: z.array(TriggerActionSchema).min(1).max(20),
    ext: ExtSchema.optional(),
})
    .strict();
export const LogicSchema = z
    .object({
    calculated: z.array(CalculatedValueSchema).max(1000).optional(),
    triggers: z.array(TriggerSchema).max(500).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Element base parts (docs/04 §9)
// ---------------------------------------------------------------------------
export const ValidatorSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('regex'),
        pattern: z.string().min(1).max(500),
        message: LocalizedStringSchema.optional(),
        severity: SeveritySchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('range'),
        min: z.union([z.number(), z.string().max(40)]).optional(),
        max: z.union([z.number(), z.string().max(40)]).optional(),
        message: LocalizedStringSchema.optional(),
        severity: SeveritySchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('length'),
        min: z.number().int().min(0).optional(),
        max: z.number().int().min(0).optional(),
        message: LocalizedStringSchema.optional(),
        severity: SeveritySchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('expr'),
        expr: ExprSchema,
        message: LocalizedStringSchema.optional(),
        severity: SeveritySchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('custom'),
        id: z.string().regex(HOST_ID_PATTERN),
        message: LocalizedStringSchema.optional(),
        severity: SeveritySchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
]);
/** Presentation hints are an open extension point — renderers ignore what they don't know. */
export const AppearanceSchema = z
    .object({
    variant: z.string().max(40).optional(),
    columns: z.number().int().min(1).max(6).optional(),
    size: z.enum(['sm', 'md', 'lg']).optional(),
    ext: ExtSchema.optional(),
})
    .passthrough();
export const BindSchema = z
    .object({
    sensitive: z.boolean().optional(),
    saveIncomplete: z.boolean().optional(),
    trackChanges: z.boolean().optional(),
    index: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const DefaultSchema = z.union([
    z
        .object({ value: z.unknown() })
        .strict()
        // `z.unknown()` infers an *optional* key, so `{}` would otherwise satisfy this
        // branch. Require the key, then re-shape it so the inferred type is the
        // `{ value: unknown }` (required) that `../types.ts` declares.
        .refine((v) => Object.hasOwn(v, 'value'), { message: '`default.value` is required' })
        .transform((v) => ({ value: v.value })),
    z.object({ expr: ExprSchema }).strict(),
]);
const BooleanOrExprSchema = z.union([z.boolean(), ExprSchema]);
/** Element types with no type-specific props (checkbox, hidden, calculate). */
export const EmptyPropsSchema = z.object({ ext: ExtSchema.optional() }).strict();
/** Host-defined props of an `x:` element: any JSON object, minus pollution keys. */
export const CustomPropsSchema = z.record(z.string(), z.unknown()).superRefine((value, ctx) => {
    for (const key of Object.keys(value)) {
        if (FORBIDDEN_KEYS.includes(key)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: [key], message: `Forbidden key "${key}"` });
        }
    }
});
// ---------------------------------------------------------------------------
// Per-type props (docs/04 §10)
// ---------------------------------------------------------------------------
export const TextPropsSchema = z
    .object({
    multiline: z.boolean().optional(),
    format: z.enum(['none', 'email', 'phone', 'url']).optional(),
    maxLength: z.number().int().min(1).max(65536).optional(),
    mask: z.string().min(1).max(64).optional(),
    placeholder: LocalizedStringSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const NumberPropsSchema = z
    .object({
    kind: z.enum(['integer', 'decimal']).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().gt(0).optional(),
    unit: LocalizedStringSchema.optional(),
    thousandsSeparator: z.boolean().optional(),
    placeholder: LocalizedStringSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const DatePropsSchema = z
    .object({
    min: z.string().max(40).optional(),
    max: z.string().max(40).optional(),
    calendar: z.enum(['gregorian', 'hijri']).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
const otherSchema = z
    .object({
    enabled: z.boolean(),
    label: LocalizedStringSchema.optional(),
    value: ChoiceValueSchema.optional(),
})
    .strict();
export const SelectOnePropsSchema = z
    .object({
    list: NameSchema.optional(),
    choices: z.array(ChoiceSchema).max(10000).optional(),
    choiceFilter: ExprSchema.optional(),
    search: z.boolean().optional(),
    randomize: z.boolean().optional(),
    other: otherSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const SelectMultiplePropsSchema = SelectOnePropsSchema.extend({
    minSelected: z.number().int().min(0).optional(),
    maxSelected: z.number().int().min(1).optional(),
    exclusive: z.array(ChoiceValueSchema).optional(),
}).strict();
export const RankPropsSchema = z
    .object({
    list: NameSchema.optional(),
    choices: z.array(ChoiceSchema).min(2).max(50).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const RatingPropsSchema = z
    .object({
    max: z.number().int().min(2).max(10).optional(),
    icon: z.enum(['star', 'number', 'smiley']).optional(),
    labels: z
        .object({ min: LocalizedStringSchema.optional(), max: LocalizedStringSchema.optional() })
        .strict()
        .optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const RangePropsSchema = z
    .object({
    // Required here because `../types.ts` declares them required.
    min: z.number(),
    max: z.number(),
    step: z.number().gt(0).optional(),
    showValue: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const ConsentPropsSchema = z
    .object({
    text: LocalizedStringSchema,
    textVersion: z.string().min(1).max(32),
    method: z.enum(['tap', 'signature', 'verbal']).optional(),
    allowWithdraw: z.boolean().optional(),
    onWithdraw: z.enum(['clearSensitive', 'keep']).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const MatrixColumnsSchema = z.discriminatedUnion('type', [
    z
        .object({
        type: z.literal('select_one'),
        list: NameSchema.optional(),
        choices: z.array(ChoiceSchema).min(1).max(50).optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('number'),
        kind: z.enum(['integer', 'decimal']).optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
    z
        .object({
        type: z.literal('text'),
        maxLength: z.number().int().min(1).max(4000).optional(),
        ext: ExtSchema.optional(),
    })
        .strict(),
]);
export const MatrixPropsSchema = z
    .object({
    rows: z
        .array(z
        .object({
        value: ChoiceValueSchema,
        label: LocalizedStringSchema,
        relevant: ExprSchema.optional(),
        ext: ExtSchema.optional(),
    })
        .strict())
        .max(100),
    columns: MatrixColumnsSchema,
    requiredRows: z.enum(['all', 'any', 'none']).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const GeopointPropsSchema = z
    .object({
    accuracyThreshold: z.number().min(0).max(10000).optional(),
    warningThreshold: z.number().min(0).max(100000).optional(),
    autoCapture: z.boolean().optional(),
    allowManual: z.boolean().optional(),
    map: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const GeoLinePropsSchema = z
    .object({
    mode: z.enum(['manual', 'auto']).optional(),
    intervalSeconds: z.number().int().min(1).max(3600).optional(),
    accuracyThreshold: z.number().min(0).max(10000).optional(),
    warningThreshold: z.number().min(0).max(100000).optional(),
    allowManual: z.boolean().optional(),
    map: z.boolean().optional(),
    minPoints: z.number().int().min(2).max(10000).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const ImagePropsSchema = z
    .object({
    source: z.enum(['camera', 'gallery', 'both']).optional(),
    maxPixels: z.number().int().min(64).max(8192).optional(),
    quality: z.number().gt(0).max(1).optional(),
    annotate: z.boolean().optional(),
    geotag: z.boolean().optional(),
    multiple: z.boolean().optional(),
    maxCount: z.number().int().min(1).max(50).optional(),
    maxBytes: z.number().int().min(1024).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const MediaCapturePropsSchema = z
    .object({
    maxDurationSeconds: z.number().int().min(1).max(14400).optional(),
    accept: z.array(z.string().max(100)).optional(),
    maxBytes: z.number().int().min(1024).optional(),
    multiple: z.boolean().optional(),
    maxCount: z.number().int().min(1).max(50).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const BarcodePropsSchema = z
    .object({
    formats: z
        .array(z.enum([
        'qr_code',
        'code_128',
        'code_39',
        'code_93',
        'codabar',
        'ean_13',
        'ean_8',
        'upc_a',
        'upc_e',
        'itf',
        'pdf417',
        'data_matrix',
        'aztec',
    ]))
        .min(1)
        .optional(),
    allowManual: z.boolean().optional(),
    multiple: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const SignaturePropsSchema = z
    .object({
    penColor: z
        .string()
        .regex(/^(#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})|(rgb|rgba|hsl|hsla)\([^)]*\))$/)
        .optional(),
    maxBytes: z.number().int().min(1024).max(2097152).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const NotePropsSchema = z
    .object({
    style: z.enum(['info', 'warning', 'success']).optional(),
    collapsible: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const GroupPropsSchema = z.object({ nestData: z.boolean().optional(), ext: ExtSchema.optional() }).strict();
export const RepeatPropsSchema = z
    .object({
    min: z.number().int().min(0).optional(),
    max: z.number().int().min(1).optional(),
    count: ExprSchema.optional(),
    addLabel: LocalizedStringSchema.optional(),
    removeLabel: LocalizedStringSchema.optional(),
    itemLabel: ExprSchema.optional(),
    keyField: NameSchema.optional(),
    confirmDelete: z.boolean().optional(),
    allowReorder: z.boolean().optional(),
    ext: ExtSchema.optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Element union (docs/04 §9, §10)
// ---------------------------------------------------------------------------
const elementBaseShape = {
    name: NameSchema,
    label: LocalizedStringSchema.optional(),
    hint: LocalizedStringSchema.optional(),
    guidance: LocalizedStringSchema.optional(),
    media: MediaSchema.optional(),
    required: BooleanOrExprSchema.optional(),
    requiredMessage: LocalizedStringSchema.optional(),
    relevant: ExprSchema.optional(),
    readonly: BooleanOrExprSchema.optional(),
    default: DefaultSchema.optional(),
    calculate: ExprSchema.optional(),
    constraint: ExprSchema.optional(),
    constraintMessage: LocalizedStringSchema.optional(),
    validators: z.array(ValidatorSchema).optional(),
    appearance: AppearanceSchema.optional(),
    bind: BindSchema.optional(),
    ext: ExtSchema.optional(),
};
/** `label` is intentionally optional everywhere: a missing one is `W_LABEL_MISSING` (docs/04 §9). */
function leafElement(type, props) {
    return z.object({ ...elementBaseShape, type: z.literal(type), props: props.optional() }).strict();
}
/**
 * `elements` is referenced through `z.lazy` so the module can be initialised in one
 * pass despite group/repeat recursion; the annotation on `ElementSchema` supplies
 * the output type that inference cannot compute for a cyclic schema.
 */
const childElementsSchema = z.array(z.lazy(() => ElementSchema)).max(2000);
export const TextElementSchema = leafElement('text', TextPropsSchema);
export const NumberElementSchema = leafElement('number', NumberPropsSchema);
export const DateElementSchema = leafElement('date', DatePropsSchema);
export const TimeElementSchema = leafElement('time', DatePropsSchema);
export const DatetimeElementSchema = leafElement('datetime', DatePropsSchema);
export const SelectOneElementSchema = leafElement('select_one', SelectOnePropsSchema);
export const SelectMultipleElementSchema = leafElement('select_multiple', SelectMultiplePropsSchema);
export const RankElementSchema = leafElement('rank', RankPropsSchema);
export const RatingElementSchema = leafElement('rating', RatingPropsSchema);
export const RangeElementSchema = leafElement('range', RangePropsSchema);
export const CheckboxElementSchema = leafElement('checkbox', EmptyPropsSchema);
export const ConsentElementSchema = leafElement('consent', ConsentPropsSchema);
export const MatrixElementSchema = leafElement('matrix', MatrixPropsSchema);
export const GeopointElementSchema = leafElement('geopoint', GeopointPropsSchema);
export const GeotraceElementSchema = leafElement('geotrace', GeoLinePropsSchema);
export const GeoshapeElementSchema = leafElement('geoshape', GeoLinePropsSchema);
export const ImageElementSchema = leafElement('image', ImagePropsSchema);
export const AudioElementSchema = leafElement('audio', MediaCapturePropsSchema);
export const VideoElementSchema = leafElement('video', MediaCapturePropsSchema);
export const FileElementSchema = leafElement('file', MediaCapturePropsSchema);
export const BarcodeElementSchema = leafElement('barcode', BarcodePropsSchema);
export const SignatureElementSchema = leafElement('signature', SignaturePropsSchema);
export const NoteElementSchema = leafElement('note', NotePropsSchema);
export const HiddenElementSchema = leafElement('hidden', EmptyPropsSchema);
export const CalculateElementSchema = leafElement('calculate', EmptyPropsSchema);
export const GroupElementSchema = z
    .object({
    ...elementBaseShape,
    type: z.literal('group'),
    props: GroupPropsSchema.optional(),
    elements: childElementsSchema,
})
    .strict();
export const RepeatElementSchema = z
    .object({
    ...elementBaseShape,
    type: z.literal('repeat'),
    props: RepeatPropsSchema.optional(),
    elements: childElementsSchema,
})
    .strict();
/** Host-registered element. `props` stay untouched; base properties apply unchanged. */
export const CustomElementSchema = z
    .object({
    ...elementBaseShape,
    type: z.custom((v) => typeof v === 'string' && CUSTOM_TYPE_PATTERN.test(v), {
        message: 'Custom element type must match ^x:[a-z][a-z0-9-]*$',
    }),
    props: CustomPropsSchema.optional(),
})
    .strict();
export const BuiltInElementSchema = z.discriminatedUnion('type', [
    TextElementSchema,
    NumberElementSchema,
    DateElementSchema,
    TimeElementSchema,
    DatetimeElementSchema,
    SelectOneElementSchema,
    SelectMultipleElementSchema,
    RankElementSchema,
    RatingElementSchema,
    RangeElementSchema,
    CheckboxElementSchema,
    ConsentElementSchema,
    MatrixElementSchema,
    GeopointElementSchema,
    GeotraceElementSchema,
    GeoshapeElementSchema,
    ImageElementSchema,
    AudioElementSchema,
    VideoElementSchema,
    FileElementSchema,
    BarcodeElementSchema,
    SignatureElementSchema,
    NoteElementSchema,
    HiddenElementSchema,
    CalculateElementSchema,
    GroupElementSchema,
    RepeatElementSchema,
]);
/** Built-in element `type` values, in schema order (used for `E_UNKNOWN_TYPE` hints). */
export const BUILT_IN_ELEMENT_TYPES = [
    'text',
    'number',
    'date',
    'time',
    'datetime',
    'select_one',
    'select_multiple',
    'rank',
    'rating',
    'range',
    'checkbox',
    'consent',
    'matrix',
    'geopoint',
    'geotrace',
    'geoshape',
    'image',
    'audio',
    'video',
    'file',
    'barcode',
    'signature',
    'note',
    'hidden',
    'calculate',
    'group',
    'repeat',
];
/** Element types that carry `elements` (docs/04 §9: `E_ELEMENTS_ON_LEAF` elsewhere). */
export const CONTAINER_ELEMENT_TYPES = ['group', 'repeat'];
/**
 * The whole element union. The explicit annotation is both the recursion break and
 * the compile-time proof that every branch produces an `Element` from `../types.ts`.
 */
export const ElementSchema = z.union([
    BuiltInElementSchema,
    CustomElementSchema,
]);
// ---------------------------------------------------------------------------
// pages and the root document (docs/04 §2, §7)
// ---------------------------------------------------------------------------
export const PageSchema = z
    .object({
    id: PageIdSchema,
    title: LocalizedStringSchema.optional(),
    description: LocalizedStringSchema.optional(),
    relevant: ExprSchema.optional(),
    elements: z.array(ElementSchema).max(2000),
    ext: ExtSchema.optional(),
})
    .strict();
export const RequiresSchema = z
    .object({
    rasd: z.string().max(32).optional(),
    features: z.array(z.string().regex(/^(type|fn|x|cap):[a-zA-Z0-9_:-]{1,64}$/)).max(64).optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const FormMetaSchema = z
    .object({
    title: LocalizedStringSchema,
    description: LocalizedStringSchema.optional(),
    tags: z.array(z.string().min(1).max(40)).max(32).optional(),
    author: z.string().max(200).optional(),
    createdAt: IsoDateTimeSchema.optional(),
    updatedAt: IsoDateTimeSchema.optional(),
    changelog: LocalizedStringSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
export const FormDefinitionSchema = z
    .object({
    $schema: z.string().optional(),
    rasd: z.string().regex(RASD_VERSION_PATTERN),
    id: z.string().regex(FORM_ID_PATTERN),
    version: z.string().min(1).max(32),
    requires: RequiresSchema.optional(),
    meta: FormMetaSchema,
    settings: FormSettingsSchema,
    choiceLists: z.record(NameSchema, ChoiceListSchema).optional(),
    datasets: z.array(DatasetSchema).max(100).optional(),
    // `E_NO_PAGES` is raised by the semantic pass so it carries its own code.
    pages: z.array(PageSchema).max(1000),
    logic: LogicSchema.optional(),
    ext: ExtSchema.optional(),
})
    .strict();
/** Value witness so the proof is emitted (and checked) even under `isolatedDeclarations`. */
export const assertMatchesTypes = {
    localizedString: true,
    media: true,
    geo: true,
    attachmentRef: true,
    choice: true,
    choiceList: true,
    dataset: true,
    calculatedValue: true,
    trigger: true,
    triggerAction: true,
    validator: true,
    element: true,
    page: true,
    settingsMinusAudit: true,
    auditMinusPriority: true,
    formDefinition: true,
};
//# sourceMappingURL=form.js.map